import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';

function FAQ() {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqs = [
    {
      question: 'What makes your company different?',
      answer: 'Where many firms compete on price or speed, we compete on excellence. Every project is built with uncompromising craftsmanship, premium materials, and a focus on long-term performance. Clients choose us because they know they\'re investing in quality that lasts.'
    },
    {
      question: 'How do you ensure consistency across the country?',
      answer: 'We follow a proven, nationwide quality-control system. Our in-house teams and trusted partners are thoroughly vetted and trained to our standards. Whether your project is in a major metro or a smaller market, you can expect the same level of precision and attention to detail.'
    },
    {
      question: 'How do you keep projects on schedule without sacrificing quality?',
      answer: 'We believe true efficiency means delivering on time and at the highest standard. Our teams develop accurate schedules, anticipate potential challenges, and resolve issues proactively. Quality is never compromised.'
    },
    {
      question: 'Who will oversee my project?',
      answer: 'Every client is assigned a dedicated project manager who serves as a single point of contact from start to finish. This ensures clear communication, accountability, and peace of mind throughout the entire process.'
    },
    {
      question: 'What types of projects do you specialize in?',
      answer: 'We deliver a wide range of commercial projects, with expertise in retail, restaurants, office spaces, and multi-location rollouts for national brands. Our specialty is maintaining consistent quality and brand standards across every site we build.'
    },
    {
      question: 'How do you handle varying codes and regulations?',
      answer: 'Our teams have extensive experience navigating local codes and regulations across all 50 states. We work closely with local officials while upholding our own high-quality standards, ensuring every project is both fully compliant and exceptionally built.'
    },
    {
      question: 'Do you provide warranties or post-construction support?',
      answer: 'Yes. We stand behind our work with warranties and ongoing support after completion. Our commitment doesn\'t end at project delivery—it extends to long-term performance and client satisfaction.'
    }
  ];

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="bg-black py-24 relative overflow-hidden">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <span className="text-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 block">Common Queries</span>
          <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Frequently Asked <span className="text-gold">Questions</span>
          </h1>
          <p className="text-gray-300 text-xl max-w-3xl mx-auto">
            Find answers to common questions about our services and processes
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30" data-testid="faq-section">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-black border border-gold/20 rounded-lg overflow-hidden hover:border-gold/50 transition-all"
                data-testid={`faq-item-${index}`}
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full flex justify-between items-center p-8 text-left hover:bg-gray-900/50 transition-colors group"
                  data-testid={`faq-question-${index}`}
                >
                  <span className="text-white font-semibold text-xl pr-4">{faq.question}</span>
                  <ChevronDown 
                    className={`w-6 h-6 text-gold transition-transform flex-shrink-0 group-hover:scale-110 ${
                      openFAQ === index ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>
                {openFAQ === index && (
                  <div className="px-8 pb-8 text-gray-300 leading-relaxed text-lg" data-testid={`faq-answer-${index}`}>
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Still Have <span className="text-gold">Questions?</span>
          </h3>
          <p className="text-gray-300 text-xl mb-10">
            We're here to help. Contact us and we'll get back to you promptly.
          </p>
          <Link 
            to="/contact"
            className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}

export default FAQ;